
import React, { useState, useEffect } from 'react';

const StepperInput: React.FC<{ 
    value: number; 
    onChange: (val: number) => void; 
    min?: number; 
    max?: number; 
    step?: number;
    className?: string;
    disabled?: boolean;
}> = ({ value, onChange, min = 0, max = 999, step = 1, className = "", disabled = false }) => {
    const [inputValue, setInputValue] = useState<string>(value.toString());
    // Round to the same number of decimal places as `step` to avoid floating-point
    // drift (e.g. 1.1 + 0.1 = 1.2000000000000002) when stepping repeatedly.
    const decimals = (step.toString().split('.')[1] || '').length;
    const round = (v: number) => Number(v.toFixed(decimals));

    useEffect(() => {
        setInputValue(value.toString());
    }, [value]);

    const handleDecrement = () => {
        const newValue = Math.max(min, round(value - step));
        onChange(newValue);
    };

    const handleIncrement = () => {
        const newValue = Math.min(max, round(value + step));
        onChange(newValue);
    };

    const handleBlur = () => {
        let val = parseFloat(inputValue);
        if (isNaN(val)) val = min;
        let finalVal = round(Math.max(min, Math.min(max, val)));
        setInputValue(finalVal.toString());
        if (finalVal !== value) {
            onChange(finalVal);
        }
    };

    return (
        <div className={`flex items-center h-12 w-full border-2 border-gray-300 rounded-md overflow-hidden shadow-sm bg-white ${className}`}>
            <button 
                type="button"
                disabled={disabled}
                onClick={handleDecrement}
                className="w-12 h-full flex-shrink-0 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:hover:bg-white disabled:active:bg-white disabled:cursor-not-allowed flex items-center justify-center border-r-2 border-gray-300 text-gray-700 font-black text-2xl transition-colors focus:outline-none"
                tabIndex={-1}
            >
                −
            </button>
            <input 
                type="number" 
                disabled={disabled}
                value={inputValue} 
                onChange={(e) => { const v = e.target.value; setInputValue(v); const val = parseFloat(v); if(!isNaN(val)) onChange(val); }}
                onBlur={handleBlur}
                className="flex-1 w-full h-full text-center text-lg font-black text-gray-800 bg-white disabled:cursor-not-allowed focus:outline-none min-w-0 appearance-none m-0 p-0 border-0"
                style={{ colorScheme: 'light', MozAppearance: 'textfield', WebkitAppearance: 'none' }}
                step={step}
            />
            <button 
                type="button"
                disabled={disabled}
                onClick={handleIncrement}
                className="w-12 h-full flex-shrink-0 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:hover:bg-white disabled:active:bg-white disabled:cursor-not-allowed flex items-center justify-center border-l-2 border-gray-300 text-gray-700 font-black text-2xl transition-colors focus:outline-none"
                tabIndex={-1}
            >
                +
            </button>
            <style>{`
                input[type=number]::-webkit-inner-spin-button, 
                input[type=number]::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
            `}</style>
        </div>
    );
};

export default StepperInput;
